import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, ScatterChart, Scatter, ZAxis, AreaChart, Area, Legend } from 'recharts';
import { BarChart3, JapaneseYen, MessageCircle, Send, Bot, User, Loader, HelpCircle, X } from 'lucide-react';
import axios from 'axios';
import { apiClient, API_ENDPOINTS } from '../config/api';

interface DataAnalysisWithChatProps {
  fileId: string | null;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const VIEW_TABS = [
  { key: 'overview', label: '概要' },
  { key: 'jv', label: 'JV/ネットワーク' },
  { key: 'share', label: 'シェア/リテンション' }
] as const;

const VIEW_NOTES: Record<string, string> = {
  overview: '全体の件数・金額・方式・時系列などの基本指標を俯瞰できます。',
  competition: '予定価格に対する落札率と競合社数の関係で、過大/過小落札や競争の激しさを把握します。',
  schedule: '工期の長さと延長率、どの部局・案件が延びやすいかを確認します。',
  quality: '成績・難易度・金額・競合数など数値指標の相関を見て、品質とリスクの関係を探ります。',
  jv: 'JV比率の推移・規模・トレンド・組み合わせネットワークで協業パターンを把握します。',
  share: '年度別シェア推移、集中度、連続受注ストリークで市場支配度とロイヤルティを確認します。',
  cost: '当初/新請負額や支払限度額との差分から増減率分布と増額案件を把握します。'
};

// よくある質問
const SUMMARY_FAQ_LABEL = '現在の条件（全件）で、\n概要の重要ポイントをまとめてください。';
const SUMMARY_FAQ_PROMPT = '現在の条件（全件）で、概要の重要ポイントをまとめてください。';
const FAQ_QUESTIONS = [
  SUMMARY_FAQ_LABEL,
  '総件数はいくつですか？',
  '最高金額はいくらですか？',
  '平均金額を教えて',
  'どのカテゴリが最も多い？',
  'データの概要を教えて',
  '上位ランキングは？',
  '金額の分布を見たい',
  '時系列の傾向は？'
];

type AvailableFilters = Record<string, { column: string; values: string[] }>;
type MultiSelectFilterKey = 'department' | 'work_type' | 'business_type' | 'project_category';
type SelectedFilters = {
  department?: string[];
  work_type?: string[];
  business_type?: string[];
  contractor?: string;
  year?: string;
  yearStart?: string;
  yearEnd?: string;
  project_category?: string[];
  bid_method?: string;
};

const MULTI_SELECT_FILTER_KEYS = new Set<MultiSelectFilterKey>(['department', 'project_category', 'work_type', 'business_type']);

const DataAnalysisWithChat: React.FC<DataAnalysisWithChatProps> = ({ fileId }) => {
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [availableFilters, setAvailableFilters] = useState<AvailableFilters>({});
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});
  const [trendGranularity, setTrendGranularity] = useState<'year' | 'quarter' | 'month'>('year');
  const [currentFileName, setCurrentFileName] = useState<string>('');
  const [contractorSearchTerm, setContractorSearchTerm] = useState('');
  const [showContractorSearchResults, setShowContractorSearchResults] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeView, setActiveView] = useState<'overview' | 'competition' | 'schedule' | 'quality' | 'jv' | 'share' | 'cost'>('overview');
  const [selectedDetail, setSelectedDetail] = useState<{ title: string; rows: { label: string; value: string }[] } | null>(null);
  const contractorSearchRef = useRef<HTMLDivElement | null>(null);
  const [userGranularityChosen, setUserGranularityChosen] = useState(false);

  const contractorOptions = useMemo(() => {
    return availableFilters.contractor?.values || [];
  }, [availableFilters]);

  const contractorSearchResults = useMemo(() => {
    if (!contractorSearchTerm.trim()) {
      return [] as string[];
    }
    const keyword = contractorSearchTerm.trim().toLowerCase();
    const matches = contractorOptions.filter((name) => name.toLowerCase().includes(keyword));
    return matches.slice(0, 30);
  }, [contractorSearchTerm, contractorOptions]);

  const currentViewLabel = useMemo(() => {
    const found = VIEW_TABS.find((item) => item.key === activeView);
    return found ? found.label : activeView;
  }, [activeView]);

  const trackUsageEvent = useCallback(async (payload: Record<string, unknown>) => {
    if (!fileId) {
      return;
    }
    try {
      await apiClient.post(API_ENDPOINTS.usageEvents, {
        fileId,
        ...payload,
      });
    } catch (_error) {
      // 集計失敗で本来の分析操作は止めない。
    }
  }, [fileId]);

  const buildFilterTrackingPayload = useCallback((eventType: 'filter_submit' | 'chat_with_filters', triggerAction: 'reanalyze' | 'chat_with_filters') => ({
    eventType,
    triggerAction,
    fileName: currentFileName || undefined,
    viewName: currentViewLabel,
    yearStart: selectedFilters.yearStart,
    yearEnd: selectedFilters.yearEnd,
    contractorName: selectedFilters.contractor,
    contractorInput: contractorSearchTerm.trim() || undefined,
    departments: selectedFilters.department || [],
    businesses: selectedFilters.project_category || [],
    workTypes: selectedFilters.work_type || [],
  }), [contractorSearchTerm, currentFileName, currentViewLabel, selectedFilters]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (selectedFilters.contractor) {
      setContractorSearchTerm(selectedFilters.contractor);
    } else if (!showContractorSearchResults) {
      setContractorSearchTerm('');
    }
  }, [selectedFilters.contractor, showContractorSearchResults]);

  useEffect(() => {
    if (!showContractorSearchResults) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (contractorSearchRef.current && !contractorSearchRef.current.contains(event.target as Node)) {
        setShowContractorSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showContractorSearchResults]);

  useEffect(() => {
    if (isChatOpen) {
      scrollToBottom();
    }
  }, [isChatOpen]);

  // 分析結果が変わったら手動選択状態をリセット
  useEffect(() => {
    setUserGranularityChosen(false);
  }, [analysisResults]);

  // デフォルトの粒度をデータ点数に応じて自動調整（手動選択後は上書きしない）
  useEffect(() => {
    if (!analysisResults || userGranularityChosen) return;
    const trends = analysisResults.metricsTrends || {};
    const yearlyLen = Array.isArray(trends.yearly) ? trends.yearly.length : 0;
    const quarterlyLen = Array.isArray(trends.quarterly) ? trends.quarterly.length : 0;

    let next: 'year' | 'quarter' | 'month' = 'year';
    if (yearlyLen <= 2) {
      next = quarterlyLen > 2 ? 'quarter' : 'month';
      if (quarterlyLen <= 2) {
        next = 'month';
      }
    }

    if (next !== trendGranularity) {
      setTrendGranularity(next);
    }
  }, [analysisResults, trendGranularity, userGranularityChosen]);

  const performAnalysis = useCallback(async (filters?: SelectedFilters) => {
    if (!fileId) return;
    
    setIsLoading(true);
    setAnalysisError(null);
    
    try {
      const response = await apiClient.post(API_ENDPOINTS.analyze, { fileId, filters });
      setAnalysisResults(response.data.results);
      setAvailableFilters(response.data.results?.availableFilters || {});
    } catch (error) {
      console.error('Analysis error:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response:', error.response?.data);
        setAnalysisError(error.response?.data?.error?.message || '分析結果を取得できませんでした。');
      } else if (error instanceof Error) {
        setAnalysisError(error.message);
      } else {
        setAnalysisError('分析結果を取得できませんでした。');
      }
      setAnalysisResults(null);
    } finally {
      setIsLoading(false);
    }
  }, [fileId]);

  useEffect(() => {
    if (fileId) {
      apiClient.get(API_ENDPOINTS.files).then((resp) => {
        const files = resp.data?.files || [];
        const file = files.find((item: any) => item.id === fileId);
        const resolvedFileName = file?.displayName || file?.fileName || '';
        setCurrentFileName(resolvedFileName);
        trackUsageEvent({
          eventType: 'file_select',
          fileName: resolvedFileName || undefined,
        });
      }).catch(() => setCurrentFileName(''));
      setSelectedFilters({});
      setIsChatOpen(false);
      setActiveView('overview');
      performAnalysis();
      const initialMessage: Message = {
        id: '1',
        type: 'assistant',
        content: 'こんにちは！データ分析結果について質問できます！例えば、「どの部局が最も契約件数が多いですか？」「契約金額の傾向を教えてください」などの質問ができます。',
        timestamp: new Date()
      };
      setMessages([initialMessage]);
    }
  }, [fileId, performAnalysis, trackUsageEvent]);

  const sendMessage = async () => {
    sendMessageWithQuestion(inputValue, false, true);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatScoreValue = (score?: number | null) => {
    if (score === null || score === undefined || Number.isNaN(score)) {
      return '-';
    }
    return Number(score).toFixed(1);
  };

  // 金額の簡略表示（「〇兆〇億〇万」表記、0の桁は省略）
  const formatAmountCompactJa = (amount: number): string => {
    if (amount === undefined || amount === null || isNaN(amount)) return '';
    const abs = Math.trunc(Math.abs(amount));
    const sign = amount < 0 ? '-' : '';
    const CHO = 1_000_000_000_000; // 1兆
    const OKU = 100_000_000;       // 1億
    const MAN = 10_000;            // 1万
    const cho = Math.floor(abs / CHO);
    const remCho = abs % CHO;
    const oku = Math.floor(remCho / OKU);
    const remOku = remCho % OKU;
    const man = Math.floor(remOku / MAN);
    const parts: string[] = [];
    if (cho > 0) parts.push(`${cho}兆`);
    if (oku > 0) parts.push(`${oku}億`);
    if (man > 0) parts.push(`${man}万`);
    if (parts.length > 0) return `${sign}${parts.join('')}`;
    // 1万未満は通常書式
    return `${sign}${new Intl.NumberFormat('ja-JP').format(abs)}`;
  };

  const fileTypeLabel = (type?: string) => {
    if (type === 'construction') return '工事';
    if (type === 'business') return '業務';
    return undefined;
  };

  const renderEmptyCard = (title: string, reason: string) => (
    <div className="chart-card chart-full-width">
      <h3 style={{ fontSize: 16 }}>{title}</h3>
      <p className="chart-note">{reason}</p>
    </div>
  );

  const competitionReason = () => {
    if (!competition?.awardColumn || !competition?.expectedColumn) {
      return '予定価格や請負金額の列が見つからないため、落札率を計算できません。';
    }
    if ((competition?.scatter || []).length === 0) {
      return '有効な落札率データ（予定価格・請負額の双方が入った行）が不足しているため、グラフを表示できません。';
    }
    return 'データが不足しているため、この視点での可視化を生成できませんでした。';
  };

  const scheduleReason = () => {
    if (!schedule?.startColumn || !schedule?.endColumn) {
      return '工期の開始・終了列が見つからないため、期間や遅延を算出できません。';
    }
    if ((schedule?.histogram || []).length === 0) {
      return '工期データの日付が不足しているため、分布を表示できません。';
    }
    return 'データが不足しているため、この視点での可視化を生成できませんでした。';
  };

  const correlationReason = () => '相関を計算できる数値列が不足しているため表示できません。';
  const jvReason = () => 'JV判定に必要な受注業者名やJV表記が見つからない、またはJV案件が含まれていません。';
  const shareReason = () => '年度または受注業者名の情報が不足しているため、市場シェア推移を算出できません。';
  const costReason = () => '当初請負額や新請負額などの金額列が不足しているため、増減率を計算できません。';

  // ローカルでFAQ質問を処理
  const processLocalFAQ = (question: string, results: any): string | null => {
    if (!results) return null;
    
    const basicStats = results.basicStats || {};
    const departmentStats = results.departmentStats || {};
    const topContractors = results.topContractors || {};
    
    // 総件数
    if (question.includes('総件数') || question.includes('何件')) {
      return `📊 総レコード数は **${basicStats.totalRecords?.toLocaleString() || 0}件** です。`;
    }
    
    // 最高金額
    if (question.includes('最高金額') || question.includes('最大')) {
      if (basicStats.maxAmount) {
        return `💰 最高金額は **${formatCurrency(basicStats.maxAmount)}** です。`;
      }
      return '金額データが見つかりませんでした。';
    }
    
    // 平均金額
    if (question.includes('平均金額') || question.includes('平均')) {
      if (basicStats.averageAmount) {
        return `📈 平均金額は **${formatCurrency(basicStats.averageAmount)}** です。`;
      }
      return '金額データが見つかりませんでした。';
    }
    
    // カテゴリ
    if (question.includes('カテゴリ') || (question.includes('どの') && question.includes('多い'))) {
      if (departmentStats.data && departmentStats.data.length > 0) {
        const top = departmentStats.data[0];
        return `🏢 最も多い${departmentStats.column || 'カテゴリ'}は「**${top.name}**」で、**${top.count}件** のレコードがあります。`;
      }
      return 'カテゴリデータが見つかりませんでした。';
    }
    
    // データの概要
    if (question.includes('概要') || question.includes('データ')) {
      let response = `📊 **データ概要**\n\n`;
      response += `• 総レコード数: ${basicStats.totalRecords?.toLocaleString() || 0}件\n`;
      
      if (basicStats.totalAmount) {
        response += `• 総金額: ${formatCurrency(basicStats.totalAmount)}\n`;
        response += `• 平均金額: ${formatCurrency(basicStats.averageAmount || 0)}\n`;
      }
      
      if (departmentStats.data && departmentStats.data.length > 0) {
        response += `• 最多${departmentStats.column || 'カテゴリ'}: ${departmentStats.data[0].name}`;
      }
      
      return response;
    }
    
    // ランキング
    if (question.includes('ランキング') || question.includes('上位')) {
      if (topContractors.data && topContractors.data.length > 0) {
        let response = `🏆 **上位${topContractors.column || 'エントリ'}ランキング**\n\n`;
        topContractors.data.slice(0, 5).forEach((item: any, index: number) => {
          const medals = ['🥇', '🥈', '🥉'];
          const prefix = index < 3 ? medals[index] : `${index + 1}.`;
          response += `${prefix} ${item.name}: ${item.count}件\n`;
        });
        return response;
      }
      return 'ランキングデータが見つかりませんでした。';
    }
    
    // 金額の分布
    if (question.includes('金額') && question.includes('分布')) {
      const amountDist = results.amountDistribution;
      if (amountDist && amountDist.data && amountDist.data.length > 0) {
        let response = `💹 **金額分布**\n\n`;
        amountDist.data.forEach((range: any) => {
          response += `• ${range.name}: ${range.count}件\n`;
        });
        return response;
      }
      return '金額分布データが見つかりませんでした。';
    }
    
    // 時系列
    if (question.includes('時系列') || question.includes('傾向')) {
      const timeSeries = results.timeSeriesData;
      if (timeSeries && timeSeries.data && timeSeries.data.length > 0) {
        const recent = timeSeries.data.slice(-3);
        let response = `📈 **時系列傾向（直近3ヶ月）**\n\n`;
        recent.forEach((item: any) => {
          response += `• ${item.month}: ${item.count}件\n`;
        });
        return response;
      }
      return '時系列データが見つかりませんでした。';
    }
    
    return null;
  };

  const handleFAQClick = (question: string) => {
    const prompt = question === SUMMARY_FAQ_LABEL ? SUMMARY_FAQ_PROMPT : question;
    setInputValue(prompt);
    const useLocalFaq = question !== SUMMARY_FAQ_LABEL;
    sendMessageWithQuestion(prompt, useLocalFaq, true);
  };

  const sendMessageWithQuestion = async (question: string, isFAQ: boolean = false, trackChatInput: boolean = false) => {
    if (!question.trim() || !fileId) return;

    if (trackChatInput) {
      await trackUsageEvent({
        eventType: 'chat_message',
        fileName: currentFileName || undefined,
        chatMessage: question.trim(),
      });
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: question,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // FAQの場合はローカル処理（思考ラグを挟む）
    if (isFAQ && analysisResults) {
      const localResponse = processLocalFAQ(question, analysisResults);
      if (localResponse) {
        setIsChatLoading(true);
        // 1.2〜2.0秒のランダムな思考時間
        const delay = 1200 + Math.floor(Math.random() * 800);
        await new Promise((r) => setTimeout(r, delay));
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: localResponse,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
        setIsChatLoading(false);
        return;
      }
    }

    // 通常のAPI処理
    setIsChatLoading(true);

    try {
      const response = await apiClient.post(API_ENDPOINTS.chat, { 
        query: question, 
        fileId,
        user_id: 'default_user',
        filters: selectedFilters
      });
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'エラーが発生しました。もう一度お試しください。',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleChatWithFilters = async () => {
    setIsChatOpen(true);
    await trackUsageEvent(buildFilterTrackingPayload('chat_with_filters', 'chat_with_filters'));
  };

  const handleYearRangeChange = (key: 'yearStart' | 'yearEnd', value: string) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const sortYearValues = (values: string[] = []) => {
    return [...values].sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      const aNum = Number.isFinite(na);
      const bNum = Number.isFinite(nb);
      if (aNum && bNum) return na - nb;
      return String(a).localeCompare(String(b), 'ja');
    });
  };

  const labelForKey = (key: string) => {
    switch (key) {
      case 'department':
        return '部局名';
      case 'work_type':
        return '工種名';
      case 'business_type':
        return '業種';
      case 'contractor':
        return '受注業者名';
      case 'yearStart':
        return '開始年度';
      case 'yearEnd':
        return '終了年度';
      case 'year':
        return '年度';
      case 'project_category':
        return '事業';
      case 'bid_method':
        return '入札方式';
      default:
        return key;
    }
  };

  const isMultiSelectFilterKey = (key: string): key is MultiSelectFilterKey => {
    return MULTI_SELECT_FILTER_KEYS.has(key as MultiSelectFilterKey);
  };

  const handleFilterChange = (key: keyof SelectedFilters, value: string) => {
    setSelectedFilters(prev => ({ ...prev, [key]: value || undefined }));
  };

  const handleMultiFilterChange = (key: MultiSelectFilterKey, event: React.ChangeEvent<HTMLSelectElement>) => {
    const values = Array.from(event.currentTarget.selectedOptions)
      .map((option) => option.value)
      .filter((value) => value.trim() !== '');
    setSelectedFilters((prev) => ({ ...prev, [key]: values.length > 0 ? values : undefined }));
  };

  const resetAllFilters = () => {
    const cleared: SelectedFilters = {};
    setSelectedFilters(cleared);
    setContractorSearchTerm('');
    setShowContractorSearchResults(false);
    performAnalysis(cleared);
  };

  const handleContractorSearchChange = (value: string) => {
    setContractorSearchTerm(value);
    setShowContractorSearchResults(Boolean(value.trim()));
  };

  const handleContractorSearchSelect = (name: string) => {
    const next: SelectedFilters = { ...selectedFilters, contractor: name };
    setSelectedFilters(next);
    performAnalysis(next);
    setContractorSearchTerm(name);
    setShowContractorSearchResults(false);
  };

  const handleChatToggle = () => {
    setIsChatOpen((prev) => !prev);
  };

  const handleChatToggleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleChatToggle();
    }
  };

  const closeChat = () => setIsChatOpen(false);

  // Convert new analysis results to old format
  const convertToOldFormat = (results: any) => {
    const basicStats = results.basicStats || {};
    const departmentStats = results.departmentStats || {};
    const contractMethodStats = results.contractMethodStats || {};
    const timeSeriesData = results.timeSeriesData || {};
    const topContractors = results.topContractors || {};
    const amountDistribution = results.amountDistribution || {};

    return {
      basicStats: {
        totalContracts: basicStats.totalRecords || 0,
        totalAmount: basicStats.totalAmount || 0,
        averageAmount: basicStats.averageAmount || 0,
        maxAmount: basicStats.maxAmount || 0,
        minAmount: basicStats.minAmount || 0
      },
      departmentStats: departmentStats.data || [],
      contractMethodStats: (contractMethodStats.data || []).map((item: any) => ({
        name: item.name,
        value: item.value || item.count || 0
      })),
      timeSeriesData: timeSeriesData.data || [],
      topContractors: topContractors.data || [],
      amountDistribution: amountDistribution.data || []
    };
  };

  if (!fileId) {
    return (
      <div className="analysis-error">
        <p>ファイルが選択されていません。</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="analysis-loading">
        <div className="loading-spinner"></div>
        <p>データを分析中...</p>
      </div>
    );
  }

  if (!analysisResults) {
    return (
      <div className="analysis-error">
        <p>{analysisError || '分析結果を取得できませんでした。'}</p>
      </div>
    );
  }

  const fileInfo = analysisResults?.fileInfo || {};
  const convertedResults = convertToOldFormat(analysisResults);
  const contractorProfile = analysisResults?.contractorProfile;
  const competition = analysisResults?.competitionAnalysis || {};
  const schedule = analysisResults?.scheduleAnalysis || {};
  const correlation = analysisResults?.correlationAnalysis || {};
  const jvNetwork = analysisResults?.jvNetwork || {};
  const shareRetention = analysisResults?.shareRetention || {};
  const costVariance = analysisResults?.costVariance || {};

  return (
    <div className="analysis-container-expanded">
      {/* Left Side - Data Analysis */}
      <div className="analysis-main-content">
        <div className="analysis-header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {fileTypeLabel(fileInfo?.fileType) && (
            <span className="current-file-badge" style={{
              background: '#fef3c7',
              color: '#92400e',
              padding: '6px 10px',
              borderRadius: 6,
              fontSize: 12,
              border: '1px solid #fcd34d'
            }}>
              【{fileTypeLabel(fileInfo?.fileType)}】
            </span>
          )}
          {fileInfo?.yearRangeLabel && (
            <span className="current-file-badge" style={{
              background: '#ecfeff',
              color: '#0f172a',
              padding: '6px 10px',
              borderRadius: 6,
              fontSize: 12,
              border: '1px solid #bae6fd'
            }}>
              {fileInfo.yearRangeLabel}年度
            </span>
          )}
          {currentFileName && (
            <span className="current-file-badge" style={{
              background: '#eef2ff',
              color: '#3730a3',
              padding: '6px 10px',
              borderRadius: 6,
              fontSize: 12,
              border: '1px solid #c7d2fe'
            }}>
              {currentFileName}
            </span>
          )}
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChart3 className="header-icon" />
            データ分析結果
          </h2>
        </div>

        {/* Filters */}
        {Object.keys(availableFilters).length > 0 && (
          <div className="filters-panel" style={{
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: '12px',
            marginBottom: '16px'
          }}>
            <div className="view-tabs" style={{ display: 'flex', gap: '8px', marginBottom: 10, flexWrap: 'wrap' }}>
              {VIEW_TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`reanalyze-button ${activeView === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveView(tab.key)}
                  style={{
                    background: activeView === tab.key ? '#3730a3' : '#e5e7eb',
                    color: activeView === tab.key ? '#fff' : '#111',
                    border: 'none',
                    padding: '8px 10px',
                    fontSize: '12px'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {(() => {
                const preferredOrder = ['year', 'department', 'project_category', 'work_type', 'business_type', 'contractor', 'bid_method'];
                const keys = Object.keys(availableFilters);
                const orderedKeys = preferredOrder.filter(k => keys.includes(k));
                return orderedKeys.map((key) => {
                  const meta = (availableFilters as any)[key];
                  if (key === 'year') {
                    const years = sortYearValues(meta.values || []);
                    return (
                      <div key="year-range" style={{ flex: '1 1 260px' }}>
                        <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                          {meta.column || labelForKey(key)}
                        </label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: 120 }}>
                            <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>開始</label>
                            <select
                              className="filter-select"
                              value={selectedFilters.yearStart || ''}
                              onChange={(e) => handleYearRangeChange('yearStart', e.target.value)}
                              style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                            >
                              <option value="">指定なし</option>
                              {years.map((v: string) => (
                                <option key={`start-${v}`} value={v}>{v}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ flex: 1, minWidth: 120 }}>
                            <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>終了</label>
                            <select
                              className="filter-select"
                              value={selectedFilters.yearEnd || ''}
                              onChange={(e) => handleYearRangeChange('yearEnd', e.target.value)}
                              style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                            >
                              <option value="">指定なし</option>
                              {years.map((v: string) => (
                                <option key={`end-${v}`} value={v}>{v}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={key} style={{ minWidth: 200 }}>
                      <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                        {meta.column || labelForKey(key)}
                      </label>
                      {isMultiSelectFilterKey(key) ? (
                        <>
                          <select
                            className="filter-select"
                            multiple
                            value={(selectedFilters[key] || []) as string[]}
                            onChange={(e) => handleMultiFilterChange(key, e)}
                            style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #e5e7eb', minHeight: 128 }}
                          >
                            {meta.values.map((v: string) => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                          </select>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                            複数選択可（Ctrl/Cmd + クリック）
                          </div>
                        </>
                      ) : (
                        <select
                          className="filter-select"
                          value={typeof (selectedFilters as Record<string, unknown>)[key] === 'string' ? String((selectedFilters as Record<string, unknown>)[key]) : ''}
                          onChange={(e) => handleFilterChange(key as keyof SelectedFilters, e.target.value)}
                          style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                        >
                          <option value="">全て</option>
                          {meta.values.map((v: string) => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                        </select>
                      )}
                      {key === 'contractor' && (
                        <div className="contractor-search" ref={contractorSearchRef}>
                          <input
                            type="text"
                            value={contractorSearchTerm}
                          onChange={(event) => handleContractorSearchChange(event.target.value)}
                          placeholder="受注業者名を検索..."
                          className="contractor-search-input"
                          onFocus={() => {
                            if (contractorSearchTerm.trim()) {
                              setShowContractorSearchResults(true);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="reanalyze-button"
                          style={{ marginTop: 6, padding: '6px 8px', fontSize: 12 }}
                          onClick={() => {
                            setSelectedFilters(prev => ({ ...prev, contractor: undefined }));
                            setContractorSearchTerm('');
                            setShowContractorSearchResults(false);
                            performAnalysis({ ...selectedFilters, contractor: undefined });
                          }}
                        >
                          受注業者名を全てに戻す
                        </button>
                          {showContractorSearchResults && (
                            <div className="contractor-search-results">
                              {contractorSearchResults.length === 0 ? (
                                <div className="contractor-search-no-results">該当する受注業者が見つかりませんでした。</div>
                              ) : (
                                contractorSearchResults.map((name) => (
                                  <button
                                    key={name}
                                    type="button"
                                    className="contractor-search-item"
                                    onClick={() => handleContractorSearchSelect(name)}
                                  >
                                    {name}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
              <div>
                <button
                  className="reanalyze-button"
                  onClick={async () => {
                    await trackUsageEvent(buildFilterTrackingPayload('filter_submit', 'reanalyze'));
                    performAnalysis(selectedFilters);
                  }}
                  disabled={isLoading}
                >
                  再分析
                </button>
                <button
                  className="reanalyze-button"
                  style={{ marginLeft: 8, background: '#0f766e', color: '#fff', border: 'none' }}
                  onClick={handleChatWithFilters}
                >
                  この条件でチャット
                </button>
                <button
                  className="reanalyze-button"
                  style={{ marginLeft: 8, background: '#6b7280', color: '#fff', border: 'none' }}
                  onClick={resetAllFilters}
                >
                  全フィルターをリセット
                </button>
              </div>
            </div>
            {/* Contractor profile card */}
            {selectedFilters.contractor && contractorProfile && (
              <div className="contractor-profile-card">
                <div className="contractor-profile-header">
                  <div className="label">受注業者</div>
                  <div className="value">{contractorProfile.contractor}</div>
                </div>

                {contractorProfile.averageScores && (
                  <div className="profile-score-row">
                    {Object.entries(contractorProfile.averageScores).map(([label, value]) => (
                      (value !== null && value !== undefined) ? (
                        <div key={label} className="profile-score-chip">
                          <span className="label">{label}</span>
                          <span className="value">{formatScoreValue(value as number)}</span>
                        </div>
                      ) : null
                    ))}
                  </div>
                )}

                {contractorProfile.categoryMetrics?.data?.length > 0 && (
                  <div className="contractor-profile-section">
                    <h4 style={{ fontSize: 14 }}>{contractorProfile.categoryMetrics.column} 別内訳</h4>
                    <div className="table-wrapper">
                      <table className="contractor-profile-table">
                        <thead>
                          <tr>
                            <th>{contractorProfile.categoryMetrics.column}</th>
                            <th>件数</th>
                            {contractorProfile.categoryMetrics.scoreColumn && <th>{contractorProfile.categoryMetrics.scoreColumn} 平均</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {contractorProfile.categoryMetrics.data.map((row: any, index: number) => (
                            <tr key={`${row.name}-${index}`}>
                              <td>{row.name}</td>
                              <td>{row.count.toLocaleString()}</td>
                              {contractorProfile.categoryMetrics.scoreColumn && (
                                <td>{formatScoreValue(row.averageScore)}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {contractorProfile.attributeBreakdown?.length > 0 && (
                  <div className="contractor-profile-attributes">
                    {contractorProfile.attributeBreakdown.map((block: any) => (
                      <div key={block.column} className="contractor-profile-section">
                        <h4 style={{ fontSize: 14 }}>{block.column} 別カウント</h4>
                        <div className="table-wrapper">
                          <table className="contractor-profile-table">
                            <thead>
                              <tr>
                                <th>{block.column}</th>
                                <th>件数</th>
                              </tr>
                            </thead>
                            <tbody>
                              {block.data.map((row: any, index: number) => (
                                <tr key={`${block.column}-${row.name}-${index}`}>
                                  <td>{row.name}</td>
                                  <td>{row.count.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="contractor-profile-section">
                  <h4 style={{ fontSize: 14 }}>業者番号 / 受注者番号</h4>
                  <div className="contractor-profile-ids">
                    {Object.entries(contractorProfile.ids || {}).map(([col, vals]: any, idx: number) => (
                      <div key={`${col}-${idx}`} className="contractor-profile-id-chip">
                        <strong>{col}:</strong> {Array.isArray(vals) ? vals.join(', ') : String(vals)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* View description */}
        <div className="analysis-note" style={{ marginTop: 12, marginBottom: 8, fontSize: 12, color: '#4b5563' }}>
          {VIEW_NOTES[activeView] || ''}
        </div>

        {activeView === 'competition' && (
          (competition && competition.scatter && competition.scatter.length > 0) ? (
            <div className="charts-grid-two-column">
              <div className="chart-card chart-full-width">
                <h3 style={{ fontSize: 16 }}>落札率 × 競争度（予定価格と請負額）</h3>
                <ResponsiveContainer width="100%" height={360}>
                  <ScatterChart margin={{ top: 16, right: 24, left: 10, bottom: 24 }}>
                    <CartesianGrid />
                    <XAxis dataKey="competitorCount" name="競合数" label={{ value: '競合社数（指名/参加）', position: 'insideBottom', offset: -5 }} />
                    <YAxis dataKey="winRate" name="落札率" tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                    <ZAxis dataKey="amount" range={[60, 400]} />
                    <Tooltip formatter={(v: any, name: any) => name === '落札率' ? `${(Number(v) * 100).toFixed(1)}%` : new Intl.NumberFormat().format(Number(v))} />
                    <Scatter data={competition.scatter} fill="#6366f1" />
                  </ScatterChart>
                </ResponsiveContainer>
                <p className="chart-note">バブルサイズ=落札額。右下方向ほど競争が激しく落札率が低い案件。</p>
              </div>

              {(competition.bands || []).length > 0 && (
                <div className="chart-card">
                  <h3 style={{ fontSize: 16 }}>競争度別の落札率</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={competition.bands}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="band" />
                      <YAxis tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                      <Tooltip formatter={(v: any) => `${(Number(v) * 100).toFixed(1)}%`} />
                      <Bar dataKey="medianWinRate" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="chart-note">競合社数帯ごとの中央値で、競争の激しさによる価格傾向を把握。</p>
                </div>
              )}

              {(competition.outliers || []).length > 0 && (
                <div className="chart-card chart-full-width">
                  <h3 style={{ fontSize: 16 }}>乖離が大きい案件（落札率）</h3>
                  <div className="contractors-list">
                    {competition.outliers.map((o: any, idx: number) => (
                      <div
                        key={idx}
                        className="contractor-item"
                        style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px', gap: 8, cursor: 'pointer' }}
                        onClick={() => setSelectedDetail({
                          title: '落札率が乖離した案件詳細',
                          rows: [
                            { label: '案件名', value: o.name },
                            { label: '落札率', value: `${(o.winRate * 100).toFixed(1)}%` },
                            { label: '競合社数', value: `${o.competitorCount?.toLocaleString?.() || '-' }社` },
                            { label: '落札額', value: formatCurrency(o.amount) },
                            { label: '予定価格', value: o.expected ? formatCurrency(o.expected) : '―' }
                          ]
                        })}
                      >
                        <span className="contractor-name">{o.name}</span>
                        <span>{(o.winRate * 100).toFixed(1)}%</span>
                        <span>{o.competitorCount?.toLocaleString?.() || '-' }社</span>
                        <span>{formatAmountCompactJa(o.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="chart-note">落札率が高/低すぎる案件を確認し、入札戦略の見直しに利用できます。</p>
                </div>
              )}
            </div>
          ) : renderEmptyCard('競争/落札率', competitionReason())
        )}

        {activeView === 'schedule' && (
          (schedule && schedule.histogram && schedule.histogram.length > 0) ? (
            <div className="charts-grid-two-column">
              <div className="chart-card">
                <h3 style={{ fontSize: 16 }}>工期長さの分布</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={schedule.histogram}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#34d399" />
                  </BarChart>
                </ResponsiveContainer>
                <p className="chart-note">中央値: {schedule?.summary?.medianDuration?.toFixed(0)}日 / 延長率平均: {(schedule?.summary?.avgExtensionRate * 100 || 0).toFixed(1)}%</p>
              </div>

              {(schedule.delays || []).length > 0 && (
                <div className="chart-card">
                  <h3 style={{ fontSize: 16 }}>延長が大きい案件トップ</h3>
                  <div className="contractors-list">
                    {schedule.delays.map((d: any, idx: number) => (
                      <div
                        key={idx}
                        className="contractor-item"
                        style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', gap: 8, cursor: 'pointer' }}
                        onClick={() => setSelectedDetail({
                          title: '工期延長案件の詳細',
                          rows: [
                            { label: '案件名', value: d.name },
                            { label: '元工期', value: `${d.durationDays}日` },
                            { label: '延長後', value: `${d.durationDays + d.extensionDays}日` },
                            { label: '延長日数', value: `${d.extensionDays}日` },
                            { label: '延長率', value: `${(d.extensionRate * 100).toFixed(1)}%` }
                          ]
                        })}
                      >
                        <span className="contractor-name">{d.name}</span>
                        <span>{d.durationDays}日 → {d.durationDays + d.extensionDays}日</span>
                        <span>{(d.extensionRate * 100).toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                  <p className="chart-note">延長率が高い案件はリスク要因。類似条件で再発防止を検討。</p>
                </div>
              )}

              {(schedule.byDepartment || []).length > 0 && (
                <div className="chart-card chart-full-width">
                  <h3 style={{ fontSize: 16 }}>部局別 工期/延長率</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={schedule.byDepartment}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-30} textAnchor="end" height={80} />
                      <YAxis yAxisId="left" orientation="left" />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="avgDuration" name="平均工期(日)" fill="#60a5fa" />
                      <Bar yAxisId="right" dataKey="avgExtensionRate" name="延長率" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="chart-note">工期が長く延長率も高い部局に注目し、工程管理改善を検討。</p>
                </div>
              )}
            </div>
          ) : renderEmptyCard('工期・遅延', scheduleReason())
        )}

        {activeView === 'quality' && (
          (correlation && (correlation.pairs || []).length > 0) ? (
            <div className="charts-grid-two-column">
              <div className="chart-card chart-full-width">
                <h3 style={{ fontSize: 16 }}>指標相関トップ</h3>
                <div className="contractors-list">
                  {correlation.pairs.map((p: any, idx: number) => (
                    <div key={idx} className="contractor-item" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 8 }}>
                      <span className="contractor-name">{p.x}</span>
                      <span>{p.y}</span>
                      <span>{p.corr.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <p className="chart-note">相関が強い指標同士に注目し、品質・競争・コストのバランスを見直します。</p>
              </div>
            </div>
          ) : renderEmptyCard('品質/相関', correlationReason())
        )}

        {activeView === 'jv' && (() => {
          const jva = analysisResults?.jvAnalysis;
          const hasData = jva && (
            (jva.yoy && jva.yoy.length > 0) ||
            (jva.sizeBreakdown && jva.sizeBreakdown.length > 0) ||
            (jva.monthlyTrend && jva.monthlyTrend.length > 0) ||
            (jva.amountTierConcentration && jva.amountTierConcentration.length > 0) ||
            (jva.heatmap?.categories || []).length > 0
          );
          if (!hasData) return renderEmptyCard('JV/ネットワーク', jvReason());
          return (
            <div className="charts-grid-two-column">
              {(analysisResults.jvAnalysis.yoy || []).length > 0 && (
                <div className="chart-card">
                  <h3 style={{ fontSize: 16 }}>JV比率（YoY）</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analysisResults.jvAnalysis.yoy}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis tickFormatter={(v) => `${Math.round(v*100)}%`} />
                      <Tooltip formatter={(v: any) => `${(Number(v)*100).toFixed(1)}%`} />
                      <Line type="monotone" dataKey="countRatio" name="件数JV比率" stroke="#1f77b4" strokeWidth={2} />
                      <Line type="monotone" dataKey="amountRatio" name="金額JV比率" stroke="#ff7f0e" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {(analysisResults.jvAnalysis.sizeBreakdown || []).length > 0 && (
                <div className="chart-card">
                  <h3 style={{ fontSize: 16 }}>JV規模内訳</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={analysisResults.jvAnalysis.sizeBreakdown} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
                        {(analysisResults.jvAnalysis.sizeBreakdown || []).map((_: any, i: number) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: any) => `${v}件`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {(analysisResults.jvAnalysis.monthlyTrend || []).length > 0 && (
                <div className="chart-card chart-full-width">
                  <h3 style={{ fontSize: 16 }}>JV 月次トレンド（12ヶ月移動平均＋前年同月比）</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={analysisResults.jvAnalysis.monthlyTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis yAxisId="left" tickFormatter={(v) => `${Math.round(v*100)}%`} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${Math.round(v*100)}%`} />
                      <Tooltip formatter={(v: any, name: any) => `${(Number(v)*100).toFixed(1)}%`} />
                      <Line yAxisId="left" type="monotone" dataKey="ratio" name="JV比率" stroke="#1f77b4" strokeWidth={2} />
                      <Line yAxisId="left" type="monotone" dataKey="ma12" name="12ヶ月移動平均" stroke="#2ca02c" strokeWidth={2} />
                      <Line yAxisId="right" type="monotone" dataKey="yoy" name="前年同月比" stroke="#d62728" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="chart-note">JV比率のトレンドと前年同月比を確認し、協業の増減を把握。</p>
                </div>
              )}

              {(analysisResults.jvAnalysis.amountTierConcentration || []).length > 0 && (
                <div className="chart-card">
                  <h3 style={{ fontSize: 16 }}>金額階層 × JV（上位10%・25%のJV濃度）</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={(analysisResults.jvAnalysis.amountTierConcentration || []).map((d: any) => ({
                      tier: d.tier,
                      JV: -d.jvRatio * 100,
                      非JV: d.nonJvRatio * 100,
                    }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[-100, 100]} tickFormatter={(v) => `${Math.abs(Math.round(v))}%`} />
                      <YAxis type="category" dataKey="tier" />
                      <Tooltip formatter={(v: any, name: any) => `${Math.abs(Number(v)).toFixed(1)}%`} />
                      <Bar dataKey="JV" fill="#1f77b4" name="JV" />
                      <Bar dataKey="非JV" fill="#ff7f0e" name="非JV" />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="chart-note">高額帯でのJV割合を確認し、協業が必要な価格帯を見極めます。</p>
                </div>
              )}

              {(analysisResults.jvAnalysis.heatmap?.categories || []).length > 0 && (
                <div className="chart-card chart-full-width">
                  <h3 style={{ fontSize: 16 }}>分類×年度ヒートマップ（工種 × 年度 JV件数比）</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: `200px repeat(${(analysisResults.jvAnalysis.heatmap.years || []).length}, 80px)`, gap: 4 }}>
                      <div style={{ fontWeight: 600 }}>工種</div>
                      {(analysisResults.jvAnalysis.heatmap.years || []).map((y: string) => (
                        <div key={`h-${y}`} style={{ fontWeight: 600, textAlign: 'center' }}>{y}</div>
                      ))}
                      {(analysisResults.jvAnalysis.heatmap.categories || []).map((cat: string, i: number) => (
                        <React.Fragment key={`row-${cat}`}>
                          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat}</div>
                          {(analysisResults.jvAnalysis.heatmap.matrix[i] || []).map((v: number, j: number) => (
                            <div key={`m-${i}-${j}`} style={{
                              textAlign: 'center',
                              background: `rgba(37, 99, 235, ${Math.min(1, Math.max(0.05, v))})`,
                              color: v > 0.5 ? '#fff' : '#111',
                              borderRadius: 4,
                              padding: '6px 0'
                            }}>
                              {Math.round(v*100)}%
                            </div>
                          ))}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <p className="chart-note">工種×年度でJV比率を比較し、協業が多い領域を把握。</p>
                </div>
              )}

              {(jvNetwork.topPairs || []).length > 0 && (
                <div className="chart-card chart-full-width">
                  <h3 style={{ fontSize: 16 }}>JV組み合わせトップ</h3>
                  <div className="contractors-list">
                    {jvNetwork.topPairs.map((p: any, idx: number) => (
                      <div key={idx} className="contractor-item" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 60px', gap: 8 }}>
                        <span className="contractor-name">{p.source}</span>
                        <span>{p.target}</span>
                        <span>{p.count}件</span>
                      </div>
                    ))}
                  </div>
                  <p className="chart-note">ネットワーク規模: {jvNetwork?.networkSize?.nodes || 0}社 / {jvNetwork?.networkSize?.edges || 0}組</p>
                </div>
              )}
            </div>
          );
        })()}

        {activeView === 'share' && (
          ((shareRetention.timeline || []).length > 0 ||
            (shareRetention.hhi || []).length > 0 ||
            (shareRetention.streaks || []).length > 0) ? (
            <div className="charts-grid-two-column">
              {(shareRetention.timeline || []).length > 0 && (
                <div className="chart-card chart-full-width">
                  <h3 style={{ fontSize: 16 }}>上位シェア推移</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={shareRetention.timeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                      <Tooltip formatter={(v: any) => `${(Number(v) * 100).toFixed(1)}%`} />
                      {shareRetention.topContractors?.map((c: string, idx: number) => (
                        <Area key={c} type="monotone" dataKey={c} stackId="1" stroke={COLORS[idx % COLORS.length]} fill={COLORS[idx % COLORS.length]} />
                      ))}
                      <Area type="monotone" dataKey="Others" stackId="1" stroke="#cbd5e1" fill="#e5e7eb" />
                    </AreaChart>
                  </ResponsiveContainer>
                  <p className="chart-note">上位業者シェアの推移から集中/分散傾向を把握。</p>
                </div>
              )}

              {(shareRetention.hhi || []).length > 0 && (
                <div className="chart-card">
                  <h3 style={{ fontSize: 16 }}>市場集中度 (HHI)</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={shareRetention.hhi}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="hhi" stroke="#fb7185" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="chart-note">1に近いほど寡占、0に近いほど分散。</p>
                </div>
              )}

              {(shareRetention.streaks || []).length > 0 && (
                <div className="chart-card">
                  <h3 style={{ fontSize: 16 }}>連続受注ストリーク</h3>
                  <div className="contractors-list">
                    {shareRetention.streaks.map((s: any, idx: number) => (
                      <div key={idx} className="contractor-item" style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', gap: 8 }}>
                        <span className="contractor-name">{s.contractor}</span>
                        <span>最長 {s.longestStreak}年</span>
                        <span>最新 {s.latestYear}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : renderEmptyCard('シェア/リテンション', shareReason())
        )}

        {activeView === 'cost' && (
          ((costVariance.distribution || []).length > 0 || (costVariance.topIncreases || []).length > 0) ? (
            <div className="charts-grid-two-column">
              {(costVariance.distribution || []).length > 0 && (
                <div className="chart-card">
                  <h3 style={{ fontSize: 16 }}>契約額の増減率分布</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={costVariance.distribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="bucket" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#f97316" />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="chart-note">平均増減率: {(costVariance?.summary?.avgChangeRate * 100 || 0).toFixed(1)}%</p>
                </div>
              )}

              {(costVariance.topIncreases || []).length > 0 && (
                <div className="chart-card chart-full-width">
                  <h3 style={{ fontSize: 16 }}>増額の大きい案件</h3>
                  <div className="contractors-list">
                    {costVariance.topIncreases.map((t: any, idx: number) => (
                      <div
                        key={idx}
                        className="contractor-item"
                        style={{ display: 'grid', gridTemplateColumns: '1fr 120px 80px', gap: 8, cursor: 'pointer' }}
                        onClick={() => setSelectedDetail({
                          title: '増額案件の詳細',
                          rows: [
                            { label: '案件名', value: t.name },
                            { label: '当初請負額', value: formatCurrency(t.initial) },
                            { label: '新請負額', value: formatCurrency(t.revised) },
                            { label: '増減率', value: `${(t.changeRate * 100).toFixed(1)}%` }
                          ]
                        })}
                      >
                        <span className="contractor-name">{t.name}</span>
                        <span>{formatAmountCompactJa(t.initial)} → {formatAmountCompactJa(t.revised)}</span>
                        <span>{(t.changeRate * 100).toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : renderEmptyCard('コスト変動', costReason())
        )}

        {selectedDetail && (
          <div className="chart-card" style={{ borderColor: '#c7d2fe' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ fontSize: 16 }}>{selectedDetail.title}</h3>
              <button className="reanalyze-button" style={{ padding: '6px 10px' }} onClick={() => setSelectedDetail(null)}>閉じる</button>
            </div>
            <div className="contractors-list">
              {selectedDetail.rows.map((r, idx) => (
                <div key={idx} className="contractor-item" style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
                  <span className="contractor-name" style={{ fontWeight: 600 }}>{r.label}</span>
                  <span>{r.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeView === 'overview' && (
          <>
            {/* Basic Statistics */}
            <div className="stats-overview">
              <div className="stat-card primary">
                <div className="stat-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">総契約件数</div>
                  <div className="stat-value">{convertedResults.basicStats.totalContracts.toLocaleString()}</div>
                </div>
              </div>

              <div className="stat-card success">
                <div className="stat-icon">
                  <JapaneseYen size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">総契約金額</div>
                  <div className="stat-value">
                    <span title={formatCurrency(convertedResults.basicStats.totalAmount)}>
                      {formatAmountCompactJa(convertedResults.basicStats.totalAmount)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="stat-card info">
                <div className="stat-icon">
                  <JapaneseYen size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">平均契約金額</div>
                  <div className="stat-value">
                    <span title={formatCurrency(convertedResults.basicStats.averageAmount)}>
                      {formatAmountCompactJa(convertedResults.basicStats.averageAmount)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="stat-card warning">
                <div className="stat-icon">
                  <JapaneseYen size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">最高契約金額</div>
                  <div className="stat-value">
                    <span title={formatCurrency(convertedResults.basicStats.maxAmount)}>
                      {formatAmountCompactJa(convertedResults.basicStats.maxAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Four metrics trend (only when 年度=全て) */}
            {(() => {
              const trends = analysisResults?.metricsTrends || { yearly: [], quarterly: [], monthly: [] };
              const key = trendGranularity === 'year' ? 'yearly' : (trendGranularity === 'quarter' ? 'quarterly' : 'monthly');
              const data = (trends as any)[key] || [];
              if (!Array.isArray(data) || data.length === 0) return null;
              return (
                <div className="chart-card chart-full-width">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>主要指標の推移（{trendGranularity === 'year' ? '年度' : trendGranularity === 'quarter' ? '四半期' : '月次'}）</h3>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className={`reanalyze-button ${trendGranularity === 'year' ? 'active' : ''}`}
                        onClick={() => {
                          setUserGranularityChosen(true);
                          setTrendGranularity('year');
                        }}
                      >年度</button>
                      <button
                        className={`reanalyze-button ${trendGranularity === 'quarter' ? 'active' : ''}`}
                        onClick={() => {
                          setUserGranularityChosen(true);
                          setTrendGranularity('quarter');
                        }}
                      >四半期</button>
                      <button
                        className={`reanalyze-button ${trendGranularity === 'month' ? 'active' : ''}`}
                        onClick={() => {
                          setUserGranularityChosen(true);
                          setTrendGranularity('month');
                        }}
                      >月次</button>
                    </div>
                  </div>

                  {/* 1) 総契約件数 */}
                  <div style={{ marginTop: 12 }}>
                    <h4 style={{ marginBottom: 8 }}>総契約件数</h4>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={data} margin={{ top: 8, right: 16, left: 16, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis width={70} />
                        <Tooltip />
                        <Line type="monotone" dataKey="totalRecords" stroke="#1f77b4" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* 2) 総契約金額 */}
                  <div style={{ marginTop: 16 }}>
                    <h4 style={{ marginBottom: 8 }}>総契約金額</h4>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={data} margin={{ top: 8, right: 16, left: 16, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis width={100} tickFormatter={(v) => formatAmountCompactJa(Number(v))} />
                        <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
                        <Line type="monotone" dataKey="totalAmount" stroke="#ff7f0e" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* 3) 平均契約金額 */}
                  <div style={{ marginTop: 16 }}>
                    <h4 style={{ marginBottom: 8 }}>平均契約金額</h4>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={data} margin={{ top: 8, right: 16, left: 16, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis width={100} tickFormatter={(v) => formatAmountCompactJa(Number(v))} />
                        <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
                        <Line type="monotone" dataKey="averageAmount" stroke="#2ca02c" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* 4) 最高契約金額 */}
                  <div style={{ marginTop: 16 }}>
                    <h4 style={{ marginBottom: 8 }}>最高契約金額</h4>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={data} margin={{ top: 8, right: 16, left: 16, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis width={100} tickFormatter={(v) => formatAmountCompactJa(Number(v))} />
                        <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
                        <Line type="monotone" dataKey="maxAmount" stroke="#d62728" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })()}

            <div className="charts-grid-two-column">
              {/* Department Statistics */}
              {convertedResults.departmentStats.length > 0 && (
                <div className="chart-card">
                  <h3>部局別契約件数</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={convertedResults.departmentStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45} 
                        textAnchor="end" 
                        height={100}
                        interval={0}
                      />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0088FE" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Contract Method Distribution */}
              {convertedResults.contractMethodStats.length > 0 && (
                <div className="chart-card">
                  <h3>入札契約方式別分布</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={convertedResults.contractMethodStats}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        // TypeScript: Recharts' label render props typing may not include `percent`.
                        // Annotate as `any` to access runtime-provided fields safely.
                        label={(entry: any) => `${entry?.name}: ${entry?.percent ? (entry.percent * 100).toFixed(0) : '0'}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {convertedResults.contractMethodStats.map((entry: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Time Series Data */}
              {convertedResults.timeSeriesData.length > 0 && (
                <div className="chart-card chart-full-width">
                  <h3>時系列での契約件数推移</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={convertedResults.timeSeriesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#0088FE" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Top Contractors */}
              {convertedResults.topContractors.length > 0 && (
                <div className="chart-card">
                  <h3>上位落札者</h3>
                  <div className="contractors-list">
                    {convertedResults.topContractors.slice(0, 10).map((contractor: any, index: number) => (
                      <div
                        key={index}
                        className="contractor-item"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          const next = {
                            ...selectedFilters,
                            contractor: contractor.name,
                            department: undefined,
                            work_type: undefined,
                            business_type: undefined,
                            project_category: undefined,
                            bid_method: undefined,
                            year: undefined,
                            yearStart: undefined,
                            yearEnd: undefined
                          } as SelectedFilters;
                          setSelectedFilters(next);
                          performAnalysis(next);
                        }}
                      >
                        <span className="contractor-rank">{index + 1}</span>
                        <span className="contractor-name">{contractor.name}</span>
                        <span className="contractor-count">{contractor.count}件</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Amount Distribution */}
              {convertedResults.amountDistribution.length > 0 && (
                <div className="chart-card">
                  <h3>契約金額の分布</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={convertedResults.amountDistribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45} 
                        textAnchor="end" 
                        height={80}
                      />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#00C49F" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div
        className="chat-toggle"
        role="button"
        tabIndex={0}
        aria-label={isChatOpen ? 'AIチャットを閉じる' : 'AIチャットを開く'}
        aria-expanded={isChatOpen}
        onClick={handleChatToggle}
        onKeyDown={handleChatToggleKeyDown}
      >
        <MessageCircle className="chat-toggle-icon" size={28} />
        <span className="chat-toggle-label">AIチャットはこちら！</span>
      </div>

      {isChatOpen && (
        <div className="chat-popup">
          <div className="chat-popup-header">
            <div className="chat-popup-title">
              <MessageCircle className="chat-header-icon" />
              <span>AIチャット</span>
            </div>
            <button
              type="button"
              className="chat-popup-close"
              onClick={closeChat}
              aria-label="AIチャットを閉じる"
            >
              <X size={18} />
            </button>
          </div>

          <div className="chat-popup-body">
            <div className="chat-messages">
              {messages.map(message => (
                <div key={message.id} className={`message ${message.type}`}>
                  <div className="message-icon">
                    {message.type === 'user' ? <User size={20} /> : <Bot size={20} />}
                  </div>
                  <div className="message-content">
                    <div dangerouslySetInnerHTML={{
                      __html: message.content
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n/g, '<br>')
                        .replace(/• /g, '• ')
                    }} />
                    <span className="message-time">
                      {message.timestamp.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))}
              {isChatLoading && (
                <div className="message assistant">
                  <div className="message-icon">
                    <Bot size={20} />
                  </div>
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="faq-container">
              <div className="faq-header">
                <HelpCircle size={16} />
                <span>よくある質問</span>
              </div>
              <div className="faq-tags">
                {FAQ_QUESTIONS.map((question, index) => (
                  <button
                    key={index}
                    className="faq-tag"
                    onClick={() => handleFAQClick(question)}
                    disabled={isChatLoading}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="chat-input-container">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="データについて質問してください..."
              className="chat-input"
              disabled={isChatLoading}
            />
            <button
              onClick={sendMessage}
              className="chat-send-button"
              disabled={!inputValue.trim() || isChatLoading}
            >
              {isChatLoading ? <Loader className="animate-spin" size={20} /> : <Send size={20} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataAnalysisWithChat;
